import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  setDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import Layout from "../component/Layout";
import {
  Search,
  SlidersHorizontal,
  Eye,
  CheckCircle,
  Clock,
  Package,
  RefreshCw,
  AlertCircle,
  FileText,
  X,
} from "lucide-react";
import "./Sellers.css";

/* ── Helpers ─────────────────────────────────────────────── */
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const GRADIENT_POOL = [
  "linear-gradient(135deg,#8a6b32,#c4973e)",
  "linear-gradient(135deg,#1d4ed8,#3b82f6)",
  "linear-gradient(135deg,#065f46,#10b981)",
  "linear-gradient(135deg,#7c3aed,#a78bfa)",
  "linear-gradient(135deg,#b45309,#f59e0b)",
  "linear-gradient(135deg,#dc2626,#f87171)",
];
const getGradient = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return GRADIENT_POOL[Math.abs(h) % GRADIENT_POOL.length];
};

const isPdfUrl = (url = "") => {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase();
  return (
    lower.includes(".pdf") ||
    lower.includes("/raw/upload/") ||
    lower.includes("format=pdf") ||
    lower.includes("content_type=application/pdf") ||
    lower.match(/\.pdf(\?.*)?$/i) !== null
  );
};

// Derive verification & account status from raw seller doc
const deriveStatus = (sellerDoc, regDoc) => {
  const rawStatus = (sellerDoc.status || "pending").toLowerCase();
  let accountStatus = rawStatus;
  let verificationStatus = "pending";

  if (regDoc) {
    const hasGstin = !!regDoc.gstinNumber;
    if (rawStatus === "active" || rawStatus === "verified") {
      verificationStatus = hasGstin ? "verified" : "kyc_review";
    } else if (rawStatus === "suspended" || rawStatus === "blocked") {
      verificationStatus = hasGstin ? "verified" : "pending";
      accountStatus = rawStatus;
    } else {
      verificationStatus = hasGstin ? "kyc_review" : "pending";
    }
  }
  return { accountStatus, verificationStatus };
};

/* ── Component ───────────────────────────────────────────── */
const Sellers = () => {
  const navigate = useNavigate();

  // Raw data from Firestore
  const [rawSellers, setRawSellers] = useState([]);
  const [rawRegistrations, setRawRegistrations] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [orderCounts, setOrderCounts] = useState({});
  const [productCounts, setProductCounts] = useState({});
  const [sellerProfiles, setSellerProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [verifFilter, setVerifFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [docFilter, setDocFilter] = useState("all");
  const [kpiFilter, setKpiFilter] = useState(null);
  const [editingSegment, setEditingSegment] = useState(null);
  const [editSegmentValue, setEditSegmentValue] = useState("");

  const handleEditSegment = (seller) => {
    setEditingSegment(seller.id);
    setEditSegmentValue(seller.segment || "");
  };

  const handleSaveSegment = async (sellerId, segmentValue) => {
    if (!segmentValue) return;
    try {
      const sellerProfileRef = doc(db, "profile", sellerId);
      await setDoc(
        sellerProfileRef,
        {
          SegmentRegistration: true,
          segment: segmentValue,
        },
        { merge: true },
      );
      setEditingSegment(null);
      setEditSegmentValue("");
    } catch (err) {
      console.error("Error updating segment:", err);
      alert("Failed to update segment");
    }
  };

  const handleCancelEdit = () => {
    setEditingSegment(null);
    setEditSegmentValue("");
  };

  /* ── 1. Fetch sellers collection (real-time) ── */
  useEffect(() => {
    const q = query(collection(db, "sellers"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRawSellers(docs);
        setLoading(false);
      },
      (err) => {
        console.error("[Sellers] sellers collection error:", err);
        setError(err.message);
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  /* ── 2. Fetch seller profiles collection (real-time) ── */
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "profile"),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          map[d.id] = data;
        });
        setSellerProfiles(map);
      },
      (err) => {
        console.error("[Sellers] profile collection error:", err);
      },
    );
    return () => unsub();
  }, []);

  /* ── 2. Fetch sellerregistrations collection in real-time ── */
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "sellerregistrations"),
      (snap) => {
        const map = {};
        const regs = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          regs.push({ id: d.id, ...data });
          map[d.id] = data;
          if (data.buyerId) map[data.buyerId] = data;
          if (data.sellerId) map[data.sellerId] = data;
          if (data.organizationContact) map[data.organizationContact] = data;
          if (data.transactionalMobile) map[data.transactionalMobile] = data;
        });
        setProfiles(map);
        setRawRegistrations(regs);
      },
      (err) => {
        console.error("[Sellers] sellerregistrations error:", err);
      },
    );
    return () => unsub();
  }, []);

  /* ── 3. Fetch order counts per seller ── */
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "orderList"),
      (snap) => {
        const counts = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          const sid = data.sellerId;
          if (!sid) return;
          counts[sid] = (counts[sid] || 0) + 1;
        });
        setOrderCounts(counts);
      },
      (err) => console.error("[Sellers] orderList error:", err),
    );
    return () => unsub();
  }, []);

  /* ── 4. Fetch product counts per seller ── */
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "products"),
      (snap) => {
        const counts = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          const sid = data.sellerId;
          if (!sid) return;
          counts[sid] = (counts[sid] || 0) + 1;
        });
        setProductCounts(counts);
      },
      (err) => console.error("[Sellers] products count error:", err),
    );
    return () => unsub();
  }, []);

  /* ── 5. Merge rawSellers & rawRegistrations into enriched list ── */
  const enrichedSellers = useMemo(() => {
    // Map to keep track of processed seller IDs to prevent duplicates
    const seenIds = new Set();
    const result = [];

    // Helper to process a seller entry
    const processSeller = (s, regFromMap = null) => {
      const sid = s.sellerId || s.buyerId || s.id;
      if (!sid || seenIds.has(sid)) return;
      seenIds.add(sid);

      const reg =
        regFromMap ||
        profiles[sid] ||
        profiles[s.id] ||
        profiles[s.sellerId] ||
        profiles[s.buyerId] ||
        profiles[s.mobile] ||
        null;

      const { accountStatus, verificationStatus } = deriveStatus(s, reg);

      const name = (
        reg?.organizationName ||
        s.organizationName ||
        s.name ||
        "Unnamed Seller"
      ).trim();
      const city = (
        reg?.contactCity ||
        reg?.shippingLocation?.address?.city ||
        s.city ||
        "—"
      ).trim();
      const state = (
        reg?.contactState ||
        reg?.shippingLocation?.address?.state ||
        s.state ||
        "—"
      ).trim();
      const pinCode = (
        reg?.contactPinCode ||
        reg?.shippingLocation?.address?.postalCode ||
        s.pinCode ||
        ""
      ).trim();
      const district = (
        reg?.contactDistrict ||
        reg?.shippingLocation?.address?.district ||
        ""
      ).trim();
      const streetAddress = (reg?.streetAddress || "").trim();
      const buildingName = (reg?.buildingName || "").trim();
      const shopNumber = (reg?.shopNumber || "").trim();
      const addressString = (reg?.shippingLocation?.addressString || "").trim();

      const mobile = (
        reg?.organizationContact ||
        reg?.transactionalMobile ||
        s.mobile ||
        s.phone ||
        "—"
      ).trim();
      const email = (
        reg?.organizationEmail ||
        reg?.transactionalEmail ||
        s.email ||
        ""
      ).trim();
      const gstin = (reg?.gstinNumber || s.gstin || "").trim();
      const gstrStatus = (reg?.gstrStatus || "registered").trim();
      const sellerType = (
        reg?.businessType ||
        s.businessType ||
        "Manufacturer"
      ).trim();
      const teamSize = (reg?.teamSize || "—").trim();
      const profile = sellerProfiles[sid];
      const segment = profile?.segment || s.segment || "";

      const bankName = (reg?.bankName || "").trim();
      const accountNumber = (reg?.accountNumber || "").trim();
      const ifscCode = (reg?.ifscCode || "").trim();
      const accountHolderName = (reg?.accountHolderName || "").trim();
      const jmiOfficerID = (reg?.jmiOfficerID || "").trim();

      const dealingPersons = Array.isArray(reg?.dealingPersons)
        ? reg.dealingPersons
        : [];
      const primaryPerson = dealingPersons[0] || null;
      const owner = (primaryPerson?.fullName || s.owner || "").trim();
      const ownerRole = (primaryPerson?.role || "").trim();
      const ownerDept = (primaryPerson?.department || "").trim();

      // Document IDs and URLs (full support for businessDocumentIds and organizationPhotoIds)
      const bizDocIds = Array.isArray(reg?.businessDocumentIds)
        ? reg.businessDocumentIds
        : [];
      const bizDocUrls = Array.isArray(reg?.businessDocumentUrls)
        ? reg.businessDocumentUrls
        : [];
      const orgPhotoIds = Array.isArray(reg?.organizationPhotoIds)
        ? reg.organizationPhotoIds
        : [];
      const orgPhotoUrls = Array.isArray(reg?.organizationPhotoUrls)
        ? reg.organizationPhotoUrls
        : [];

      const allDocIds = [...bizDocIds, ...orgPhotoIds];
      const allDocUrls = [
        ...allDocIds,
        ...bizDocUrls,
        ...orgPhotoUrls,
        reg?.gstCertificateUrl,
        s?.gstCertificateUrl,
        reg?.panCardUrl,
        s?.panCardUrl,
        reg?.cancelledChequeUrl,
        s?.cancelledChequeUrl,
        ...(Array.isArray(reg?.documents)
          ? reg.documents.map((d) => d?.url || d?.name)
          : []),
      ].filter(Boolean);

      const pdfUrls = allDocUrls.filter((u) => isPdfUrl(u));
      const hasPdf = pdfUrls.length > 0;
      const pdfCount = pdfUrls.length;
      const photoCount = orgPhotoIds.length || orgPhotoUrls.length;
      const docCount = allDocUrls.length;

      const dealingNames = dealingPersons
        .map((p) => p?.fullName || "")
        .filter(Boolean)
        .join(" ");
      const dealingPhones = dealingPersons
        .map((p) => p?.contactNumber || "")
        .filter(Boolean)
        .join(" ");
      const dealingEmails = dealingPersons
        .map((p) => p?.email || "")
        .filter(Boolean)
        .join(" ");

      result.push({
        id: sid,
        mobile,
        name,
        initials: getInitials(name),
        city,
        state,
        pinCode,
        district,
        streetAddress,
        buildingName,
        shopNumber,
        addressString,
        email,
        gstin,
        gstrStatus,
        sellerType,
        teamSize,
        segment,
        owner,
        ownerRole,
        ownerDept,
        bankName,
        accountNumber,
        ifscCode,
        accountHolderName,
        jmiOfficerID,
        dealingPersons,
        dealingNames,
        dealingPhones,
        dealingEmails,
        bizDocIds,
        bizDocUrls,
        hasPdf,
        pdfCount,
        photoCount,
        docCount,
        allDocIds,
        allDocUrls,
        accountStatus,
        verificationStatus,
        orders: orderCounts[sid] || 0,
        products: productCounts[sid] || 0,
        createdAt: s.createdAt || reg?.createdAt || reg?.updatedAt,
        _raw: s,
        _reg: reg,
      });
    };

    // First process all sellers in rawSellers
    rawSellers.forEach((s) => processSeller(s));

    // Then process any registration not already present
    rawRegistrations.forEach((r) => {
      const regId = r.buyerId || r.sellerId || r.id;
      if (!seenIds.has(regId)) {
        processSeller({ id: regId, ...r }, r);
      }
    });

    return result;
  }, [
    rawSellers,
    rawRegistrations,
    profiles,
    orderCounts,
    productCounts,
    sellerProfiles,
  ]);

  /* ── KPI counts ── */
  const totalCount = enrichedSellers.length;
  const activeCount = enrichedSellers.filter(
    (s) => s.accountStatus === "active" || s.accountStatus === "verified",
  ).length;
  const pendingKycCount = enrichedSellers.filter(
    (s) =>
      s.verificationStatus === "pending" ||
      s.verificationStatus === "kyc_review",
  ).length;
  const suspendedCount = enrichedSellers.filter(
    (s) => s.accountStatus === "suspended",
  ).length;

  /* ── Comprehensive multi-token search & multi-filter logic ── */
  const filtered = useMemo(() => {
    const rawQ = search.trim().toLowerCase();
    const tokens = rawQ ? rawQ.split(/\s+/).filter(Boolean) : [];

    return enrichedSellers.filter((s) => {
      // 1. Multi-token Search Matching: All tokens must match at least one field
      const matchSearch =
        tokens.length === 0 ||
        tokens.every((token) => {
          const tokenDigits = token.replace(/\D/g, "");
          const sMobileDigits = s.mobile ? s.mobile.replace(/\D/g, "") : "";

          return (
            (s.name && s.name.toLowerCase().includes(token)) ||
            (s.id && s.id.toLowerCase().includes(token)) ||
            (s.mobile && s.mobile.toLowerCase().includes(token)) ||
            (tokenDigits.length >= 3 && sMobileDigits.includes(tokenDigits)) ||
            (s.email && s.email.toLowerCase().includes(token)) ||
            (s.gstin && s.gstin.toLowerCase().includes(token)) ||
            (s.city && s.city.toLowerCase().includes(token)) ||
            (s.state && s.state.toLowerCase().includes(token)) ||
            (s.district && s.district.toLowerCase().includes(token)) ||
            (s.pinCode && s.pinCode.toLowerCase().includes(token)) ||
            (s.buildingName && s.buildingName.toLowerCase().includes(token)) ||
            (s.streetAddress &&
              s.streetAddress.toLowerCase().includes(token)) ||
            (s.shopNumber && s.shopNumber.toLowerCase().includes(token)) ||
            (s.addressString &&
              s.addressString.toLowerCase().includes(token)) ||
            (s.owner && s.owner.toLowerCase().includes(token)) ||
            (s.ownerRole && s.ownerRole.toLowerCase().includes(token)) ||
            (s.ownerDept && s.ownerDept.toLowerCase().includes(token)) ||
            (s.dealingNames && s.dealingNames.toLowerCase().includes(token)) ||
            (tokenDigits.length >= 3 &&
              s.dealingPhones &&
              s.dealingPhones.replace(/\D/g, "").includes(tokenDigits)) ||
            (s.dealingEmails &&
              s.dealingEmails.toLowerCase().includes(token)) ||
            (s.sellerType && s.sellerType.toLowerCase().includes(token)) ||
            (s.bankName && s.bankName.toLowerCase().includes(token)) ||
            (s.ifscCode && s.ifscCode.toLowerCase().includes(token)) ||
            (s.accountHolderName &&
              s.accountHolderName.toLowerCase().includes(token)) ||
            (tokenDigits.length >= 3 &&
              s.accountNumber &&
              s.accountNumber.includes(tokenDigits)) ||
            (s.jmiOfficerID && s.jmiOfficerID.toLowerCase().includes(token)) ||
            // Semantic intent matching
            (token === "pdf" && s.hasPdf) ||
            (token === "gst" && (Boolean(s.gstin) || s.hasPdf)) ||
            ((token === "doc" ||
              token === "docs" ||
              token === "document" ||
              token === "documents" ||
              token === "kyc") &&
              s.docCount > 0) ||
            ((token === "photo" ||
              token === "photos" ||
              token === "image" ||
              token === "images") &&
              s.photoCount > 0) ||
            ((token === "bank" || token === "account") &&
              Boolean(s.bankName || s.accountNumber)) ||
            // Document IDs or URLs match
            (s.allDocIds &&
              s.allDocIds.some((id) => id.toLowerCase().includes(token))) ||
            (s.allDocUrls &&
              s.allDocUrls.some((u) => u.toLowerCase().includes(token)))
          );
        });

      // 2. Account Status Filter
      const matchStatus =
        statusFilter === "all" ||
        s.accountStatus === statusFilter ||
        (statusFilter === "active" &&
          (s.accountStatus === "active" || s.accountStatus === "verified"));

      // 3. KYC Verification Filter
      const matchVerif =
        verifFilter === "all" || s.verificationStatus === verifFilter;

      // 4. Seller Type Filter
      const matchType =
        typeFilter === "all" ||
        (s.sellerType || "").toLowerCase().includes(typeFilter.toLowerCase());

      // 5. Document Filter
      const matchDoc =
        docFilter === "all" ||
        (docFilter === "pdf" && s.hasPdf) ||
        (docFilter === "photo" && s.photoCount > 0) ||
        (docFilter === "has_docs" && s.docCount > 0) ||
        (docFilter === "no_docs" && s.docCount === 0);

      // KPI Card Quick-Filter
      const matchKpi =
        !kpiFilter ||
        (kpiFilter === "active" &&
          (s.accountStatus === "active" || s.accountStatus === "verified")) ||
        (kpiFilter === "pending" &&
          (s.verificationStatus === "pending" ||
            s.verificationStatus === "kyc_review")) ||
        (kpiFilter === "suspended" && s.accountStatus === "suspended");

      return (
        matchSearch &&
        matchStatus &&
        matchVerif &&
        matchType &&
        matchDoc &&
        matchKpi
      );
    });
  }, [
    enrichedSellers,
    search,
    statusFilter,
    verifFilter,
    typeFilter,
    docFilter,
    kpiFilter,
  ]);

  const handleKpiClick = (filterKey) => {
    if (kpiFilter === filterKey) {
      setKpiFilter(null);
      setStatusFilter("all");
      setVerifFilter("all");
    } else {
      setKpiFilter(filterKey);
      if (filterKey === "active") setStatusFilter("active");
      else if (filterKey === "pending") setVerifFilter("kyc_review");
      else if (filterKey === "suspended") setStatusFilter("suspended");
    }
  };

  const verifLabel = {
    verified: "VERIFIED",
    kyc_review: "KYC REVIEW",
    pending: "PENDING",
  };

  const verifClass = {
    verified: "verif-verified",
    kyc_review: "verif-kyc-review",
    pending: "verif-pending",
  };

  const accountPillClass = {
    active: "pill-active",
    verified: "pill-active",
    pending: "pill-pending",
    suspended: "pill-suspended",
    blocked: "pill-blocked",
  };

  /* ── Render ── */
  return (
    <Layout pageTitle="Sellers">
      <div className="buyers-page">
        {/* ── Header ── */}
        <div className="buyers-header">
          <div className="buyers-header-left">
            <h2>Sellers</h2>
            <p>
              Manage JMI's registered B2B sellers & manufacturers — live from
              Firestore
            </p>
          </div>
          <div className="buyers-header-actions">
            <button
              className="btn-outline"
              onClick={() => window.location.reload()}
            >
              <RefreshCw size={15} /> Refresh
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="buyers-error-banner">
            <AlertCircle size={16} />
            <span>
              Firestore error: {error}. Check security rules or login session.
            </span>
          </div>
        )}

        {/* ── KPI Cards ── */}
        <div className="buyers-kpi-grid">
          <div
            className={`buyers-kpi-card kpi-card-total`}
            onClick={() => {
              setKpiFilter(null);
              setStatusFilter("all");
              setVerifFilter("all");
            }}
          >
            <div className="kpi-label">Total Sellers</div>
            <div className="kpi-value">
              {loading ? "—" : totalCount.toLocaleString()}
            </div>
            <div className="kpi-hint">All registered</div>
          </div>
          <div
            className={`buyers-kpi-card kpi-card-active ${kpiFilter === "active" ? "active-filter" : ""}`}
            onClick={() => handleKpiClick("active")}
          >
            <div className="kpi-label">Active Sellers</div>
            <div className="kpi-value">{loading ? "—" : activeCount}</div>
            <div className="kpi-hint">Account active / verified</div>
          </div>
          <div
            className={`buyers-kpi-card kpi-card-pending ${kpiFilter === "pending" ? "active-filter" : ""}`}
            onClick={() => handleKpiClick("pending")}
          >
            <div className="kpi-label">Pending KYC</div>
            <div className="kpi-value">{loading ? "—" : pendingKycCount}</div>
            <div className="kpi-hint">Awaiting verification</div>
          </div>
          <div
            className={`buyers-kpi-card kpi-card-suspended ${kpiFilter === "suspended" ? "active-filter" : ""}`}
            onClick={() => handleKpiClick("suspended")}
          >
            <div className="kpi-label">Suspended</div>
            <div className="kpi-value">{loading ? "—" : suspendedCount}</div>
            <div className="kpi-hint">Temporarily halted</div>
          </div>
        </div>

        {/* ── Enhanced Filters Toolbar ── */}
        <div className="sellers-toolbar-container">
          <div className="sellers-main-toolbar">
            {/* Search Input Box */}
            <div className="sellers-search-box">
              <Search size={16} style={{ color: "#8a6b32", flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search firm, seller ID, contact, mobile, GSTIN, city, bank, PDF..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <span className="sellers-search-count-pill">
                  {filtered.length} found
                </span>
              )}
              {search && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearch("")}
                  title="Clear search"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* Filter Dropdowns */}
            <div className="sellers-filter-group">
              <SlidersHorizontal size={15} style={{ color: "#64748b" }} />

              {/* Account: All */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setKpiFilter(null);
                }}
                className={`filter-select-enhanced ${statusFilter !== "all" ? "active" : ""}`}
                title="Account Status"
              >
                <option value="all">Account: All</option>
                <option value="active">Active / Verified</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="blocked">Blocked</option>
              </select>

              {/* KYC: All */}
              <select
                value={verifFilter}
                onChange={(e) => {
                  setVerifFilter(e.target.value);
                  setKpiFilter(null);
                }}
                className={`filter-select-enhanced ${verifFilter !== "all" ? "active" : ""}`}
                title="KYC Verification Status"
              >
                <option value="all">KYC: All</option>
                <option value="verified">Verified</option>
                <option value="kyc_review">KYC Review</option>
                <option value="pending">Pending</option>
              </select>

              {/* Type: All */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className={`filter-select-enhanced ${typeFilter !== "all" ? "active" : ""}`}
                title="Seller Type"
              >
                <option value="all">Type: All</option>
                <option value="Manufacturer">Manufacturer</option>
                <option value="Wholesaler">Wholesaler</option>
                <option value="Bullion">Bullion Dealer</option>
                <option value="Artisan">Artisan / Workshop</option>
              </select>

              {/* Docs: All */}
              <select
                value={docFilter}
                onChange={(e) => setDocFilter(e.target.value)}
                className={`filter-select-enhanced ${docFilter !== "all" ? "active" : ""}`}
                title="Document Attachments"
              >
                <option value="all">Docs: All</option>
                <option value="pdf">Has PDF Certificates</option>
                <option value="photo">Has Photos</option>
                <option value="has_docs">Any Documents Attached</option>
                <option value="no_docs">Missing Documents</option>
              </select>
            </div>
          </div>

          {/* Quick Search Chips */}
          <div className="sellers-quick-chips">
            <span className="sellers-quick-label">Quick Filters:</span>
            <button
              className={`sellers-chip ${docFilter === "pdf" ? "active" : ""}`}
              onClick={() => setDocFilter(docFilter === "pdf" ? "all" : "pdf")}
            >
              <FileText size={12} /> Has GST PDF
            </button>
            <button
              className={`sellers-chip ${typeFilter === "Manufacturer" ? "active" : ""}`}
              onClick={() =>
                setTypeFilter(
                  typeFilter === "Manufacturer" ? "all" : "Manufacturer",
                )
              }
            >
              🏭 Manufacturers
            </button>
            <button
              className={`sellers-chip ${verifFilter === "kyc_review" ? "active" : ""}`}
              onClick={() =>
                setVerifFilter(
                  verifFilter === "kyc_review" ? "all" : "kyc_review",
                )
              }
            >
              ⏳ Pending KYC
            </button>
            <button
              className={`sellers-chip ${statusFilter === "active" ? "active" : ""}`}
              onClick={() =>
                setStatusFilter(statusFilter === "active" ? "all" : "active")
              }
            >
              ✓ Verified Sellers
            </button>
            <button
              className={`sellers-chip ${docFilter === "photo" ? "active" : ""}`}
              onClick={() =>
                setDocFilter(docFilter === "photo" ? "all" : "photo")
              }
            >
              📸 With Photos
            </button>
          </div>

          {/* Active Filter Tags Bar */}
          {(search.trim() ||
            statusFilter !== "all" ||
            verifFilter !== "all" ||
            typeFilter !== "all" ||
            docFilter !== "all" ||
            kpiFilter) && (
            <div className="sellers-active-bar">
              <span
                style={{ fontWeight: 600, color: "#64748b", fontSize: "11px" }}
              >
                Active Filters:
              </span>
              {search.trim() && (
                <span className="sellers-active-tag">
                  Search: "{search.trim()}"
                  <button
                    onClick={() => setSearch("")}
                    title="Clear search text"
                  >
                    <X size={11} />
                  </button>
                </span>
              )}
              {statusFilter !== "all" && (
                <span className="sellers-active-tag">
                  Account: {statusFilter}
                  <button
                    onClick={() => setStatusFilter("all")}
                    title="Remove account filter"
                  >
                    <X size={11} />
                  </button>
                </span>
              )}
              {verifFilter !== "all" && (
                <span className="sellers-active-tag">
                  KYC: {verifFilter}
                  <button
                    onClick={() => setVerifFilter("all")}
                    title="Remove KYC filter"
                  >
                    <X size={11} />
                  </button>
                </span>
              )}
              {typeFilter !== "all" && (
                <span className="sellers-active-tag">
                  Type: {typeFilter}
                  <button
                    onClick={() => setTypeFilter("all")}
                    title="Remove type filter"
                  >
                    <X size={11} />
                  </button>
                </span>
              )}
              {docFilter !== "all" && (
                <span className="sellers-active-tag">
                  Docs:{" "}
                  {docFilter === "pdf"
                    ? "Has PDF"
                    : docFilter === "photo"
                      ? "Has Photos"
                      : docFilter === "has_docs"
                        ? "Any Docs"
                        : "Missing Docs"}
                  <button
                    onClick={() => setDocFilter("all")}
                    title="Remove docs filter"
                  >
                    <X size={11} />
                  </button>
                </span>
              )}
              {kpiFilter && (
                <span className="sellers-active-tag">
                  KPI: {kpiFilter}
                  <button
                    onClick={() => setKpiFilter(null)}
                    title="Remove KPI filter"
                  >
                    <X size={11} />
                  </button>
                </span>
              )}
              <button
                className="btn-clear-all"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                  setVerifFilter("all");
                  setTypeFilter("all");
                  setDocFilter("all");
                  setKpiFilter(null);
                }}
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* ── Table Card ── */}
        <div className="buyers-table-card">
          <div className="table-meta-bar">
            <span className="results-count">
              Showing <strong>{filtered.length}</strong> of{" "}
              <strong>{totalCount}</strong> sellers
            </span>
          </div>

          {loading ? (
            <div className="buyers-loading">
              <div className="buyers-spinner" />
              <p>Fetching sellers live from Firestore…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="buyers-empty">
              <Package
                size={40}
                style={{ color: "#94a3b8", marginBottom: 10 }}
              />
              <h4>No sellers found</h4>
              <p>Try adjusting your search query or filters.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="buyers-table">
                <thead>
                  <tr>
                    <th>Seller ID</th>
                    <th>Firm / Organization</th>
                    <th>Contact Person</th>
                    <th>Mobile</th>
                    <th>Location</th>
                    <th>Type</th>
                    <th>Segment</th>
                    <th>Documents</th>
                    <th>Verification</th>
                    <th style={{ textAlign: "center" }}>Orders</th>
                    <th style={{ textAlign: "center" }}>Products</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((seller) => (
                    <tr
                      key={seller.id}
                      className="buyer-row"
                     
                    >
                      {/* Seller ID */}
                      <td>
                        <span className="buyer-id-cell"  onClick={() => navigate(`/sellers/${seller.id}`)}>{seller.id}</span>
                      </td>

                      {/* Firm & GSTIN */}
                      <td>
                        <div className="buyer-name-cell">
                          <div
                            className="buyer-avatar"
                            style={{ background: getGradient(seller.name) }}
                          >
                            {seller.initials}
                          </div>
                          <div>
                            <div className="buyer-name">{seller.name}</div>
                            {seller.gstin ? (
                              <div
                                className="buyer-owner"
                                style={{ color: "#065f46", fontWeight: 600 }}
                              >
                                GSTIN: {seller.gstin}
                              </div>
                            ) : (
                              <div
                                className="buyer-owner"
                                style={{ color: "#94a3b8" }}
                              >
                                No GSTIN
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact Person */}
                      <td>
                        {seller.owner ? (
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: "12px",
                                color: "#1e293b",
                              }}
                            >
                              {seller.owner}
                            </div>
                            {seller.ownerRole && (
                              <div
                                style={{ fontSize: "11px", color: "#64748b" }}
                              >
                                {seller.ownerRole}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8", fontSize: "12px" }}>
                            —
                          </span>
                        )}
                      </td>

                      {/* Mobile */}
                      <td>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: "12px",
                            color: "#475569",
                          }}
                        >
                          {seller.mobile}
                        </span>
                      </td>

                      {/* Location */}
                      <td>
                        <div className="location-col">
                          <span className="city">{seller.city}</span>
                          <span className="state">{seller.state}</span>
                        </div>
                      </td>

                      {/* Type */}
                      <td>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#475569",
                            fontWeight: 500,
                          }}
                        >
                          {seller.sellerType || "—"}
                        </span>
                      </td>

                      {/* Segment */}
                      <td>
                        {editingSegment === seller.id ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <select
                              value={editSegmentValue}
                              onChange={(e) => {
                                const val = e.target.value;
                                setEditSegmentValue(val);
                                if (val) {
                                  handleSaveSegment(seller.id, val);
                                }
                              }}
                              style={{
                                fontSize: "11px",
                                padding: "2px 4px",
                                borderRadius: "4px",
                                border: "1px solid #cbd5e1",
                              }}
                            >
                              <option value="">Select Segment</option>
                              <option value="gold">Gold</option>
                              <option value="silver">Silver</option>
                              <option value="platinum">Platinum</option>
                              <option value="diamond">Diamond</option>
                              <option value="GEMS">GEMS</option>
                              <option value="PEARL">PEARL</option>
                              <option value="BANDHEL">BANDHEL</option>
                              <option value="DECOR">DECOR</option>
                              <option value="BRAND">BRAND</option>
                              <option value="TOOL">TOOL</option>
                              <option value="MACHINE">MACHINE</option>
                            </select>
                            <button
                              style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                background: "#94a3b8",
                                color: "#fff",
                                border: "none",
                                borderRadius: "3px",
                                cursor: "pointer",
                              }}
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : seller.segment ? (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#475569",
                              fontWeight: 500,
                            }}
                          >
                            {seller.segment}
                            <button
                              style={{
                                marginLeft: "8px",
                                fontSize: "11px",
                                background: "transparent",
                                border: "none",
                                color: "#1d4ed8",
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSegment(seller);
                              }}
                            >
                              Edit
                            </button>
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#94a3b8",
                              fontWeight: 400,
                            }}
                          >
                            —
                            <button
                              style={{
                                marginLeft: "8px",
                                fontSize: "11px",
                                background: "transparent",
                                border: "none",
                                color: "#1d4ed8",
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSegment(seller);
                              }}
                            >
                              Set Segment
                            </button>
                          </span>
                        )}
                      </td>

                      {/* Attached Documents */}
                      <td>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          {seller.hasPdf && (
                            <span
                              className="seller-doc-badge-pill pdf"
                              title="GST / Business Proof PDF Attached"
                            >
                              <FileText size={10} /> PDF
                            </span>
                          )}
                          {seller.photoCount > 0 && (
                            <span
                              className="seller-doc-badge-pill photo"
                              title={`${seller.photoCount} Workshop / Office Photos`}
                            >
                              📸 {seller.photoCount}
                            </span>
                          )}
                          {!seller.hasPdf && seller.photoCount === 0 && (
                            <span
                              style={{ color: "#94a3b8", fontSize: "11px" }}
                            >
                              None
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Verification */}
                      <td>
                        <span
                          className={`verif-badge ${verifClass[seller.verificationStatus] || "verif-pending"}`}
                        >
                          {seller.verificationStatus === "verified" && (
                            <CheckCircle size={11} />
                          )}
                          {seller.verificationStatus === "kyc_review" && (
                            <Clock size={11} />
                          )}
                          {verifLabel[seller.verificationStatus] ||
                            seller.verificationStatus}
                        </span>
                      </td>

                      {/* Orders */}
                      <td
                        className="orders-col"
                        style={{ textAlign: "center" }}
                      >
                        {seller.orders > 0 ? (
                          seller.orders
                        ) : (
                          <span style={{ color: "#94a3b8" }}>0</span>
                        )}
                      </td>

                      {/* Products */}
                      <td
                        style={{
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "12px",
                        }}
                      >
                        {seller.products > 0 ? (
                          seller.products
                        ) : (
                          <span style={{ color: "#94a3b8" }}>0</span>
                        )}
                      </td>

                      {/* Status */}
                      <td>
                        <span
                          className={`status-pill-sm ${accountPillClass[seller.accountStatus] || "pill-pending"}`}
                        >
                          {(seller.accountStatus || "PENDING").toUpperCase()}
                        </span>
                      </td>

                      {/* Action */}
                      <td onClick={(e) => e.stopPropagation()}>
                        {seller.verificationStatus === "pending" ||
                        seller.verificationStatus === "kyc_review" ? (
                          <button
                            className="btn-review-buyer"
                            onClick={() => navigate(`/sellers/${seller.id}`)}
                          >
                            <Eye size={13} /> Review KYC
                          </button>
                        ) : (
                          <button
                            className="btn-view-buyer"
                            onClick={() => navigate(`/sellers/${seller.id}`)}
                          >
                            <Eye size={13} /> 360° View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Sellers;
